import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, message } from "antd";
import { API_METHODS } from "../../constants";
import { isEmpty } from "../../generalFunctions";
import useApi from "../../hooks/useApi";
import ServerDownResult from "../../components/common/ServerDownResult";
import { Phone, ShieldCheck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";

function UserLogin() {
  const [otpRequestResponse, otpRequest] = useApi(
    "/user/auth/otp/request",
    null,
    API_METHODS.POST
  );
  const [otpVerifyResponse, otpVerify] = useApi(
    "/user/auth/otp/verify",
    null,
    API_METHODS.POST
  );
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [messageApi, contextHolder] = message.useMessage();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phoneSentTo, setPhoneSentTo] = useState("");

  const redirectTo = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      return params.get("redirect") || "/";
    } catch {
      return "/";
    }
  }, [location.search]);

  // Redirect already-logged-in users
  useEffect(() => {
    if (localStorage.getItem("authToken")) {
      navigate(redirectTo, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizePhone = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
    return digits;
  };

  const requestOtpClicked = () => {
    form.validateFields(["phone"]).then((values) => {
      const phone = normalizePhone(values.phone);
      otpRequest({ phone });
    });
  };

  const verifyOtpClicked = () => {
    form.validateFields(["otp"]).then((values) => {
      const phone = phoneSentTo || normalizePhone(form.getFieldValue("phone"));
      otpVerify({ phone, otp: values.otp });
    });
  };

  useEffect(() => {
    if (!isEmpty(otpRequestResponse) && !otpRequestResponse.isLoading) {
      if (otpRequestResponse.data?.phone) {
        setPhoneSentTo(otpRequestResponse.data.phone);
        setStep("otp");
        messageApi.success("OTP sent to your mobile number");
        form.setFieldsValue({ otp: "" });
      }
      if (otpRequestResponse.error) {
        messageApi.error(
          otpRequestResponse.error || "Failed to send OTP. Please try again."
        );
      }
    }
  }, [otpRequestResponse, messageApi, form]);

  useEffect(() => {
    if (!isEmpty(otpVerifyResponse) && !otpVerifyResponse.isLoading) {
      if (otpVerifyResponse.data?.token) {
        localStorage.setItem("authToken", otpVerifyResponse.data.token);
        localStorage.setItem("user", JSON.stringify(otpVerifyResponse.data.user));

        messageApi.success("Logged in successfully");
        setTimeout(() => navigate(redirectTo), 400);
      }
      if (otpVerifyResponse.error) {
        messageApi.error(
          otpVerifyResponse.error || "Invalid OTP. Please try again."
        );
        form.setFieldsValue({ otp: "" });
      }
    }
  }, [otpVerifyResponse, navigate, messageApi, form, redirectTo]);

  return (
    <>
      {contextHolder}
      <HeaderComponent />

      {!isEmpty(otpRequestResponse) || !isEmpty(otpVerifyResponse) ? (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center py-12 px-4">
          <div className="w-full max-w-md">
            {/* Header with smaller logo */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-full overflow-hidden">
                  <img
                    src={logo}
                    alt="MahalaxmiPuja Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">
                    MahalaxmiPuja
                  </h1>
                  <p className="text-orange-600 font-medium text-sm">
                    Shree Mahalaxmi Ambabai
                  </p>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Login with OTP
              </h2>
              <p className="text-gray-600">
                Enter your mobile number to receive a one-time password
              </p>
            </div>

            {/* Login Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-8">
              <Form layout="vertical" form={form}>
                <Form.Item
                  label={
                    <span className="flex items-center text-gray-700 font-medium">
                      <Phone className="h-4 w-4 mr-2 text-orange-500" />
                      Mobile Number
                    </span>
                  }
                  name="phone"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your mobile number",
                    },
                    () => ({
                      validator(_, value) {
                        const digits = normalizePhone(value);
                        if (!digits || digits.length !== 10) {
                          return Promise.reject(
                            new Error("Enter a valid 10-digit mobile number")
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="10-digit mobile number"
                    prefix={<Phone className="h-4 w-4 text-gray-400" />}
                    className="h-12"
                    inputMode="numeric"
                    disabled={step === "otp"}
                  />
                </Form.Item>

                {step === "otp" && (
                  <Form.Item
                    label={
                      <span className="flex items-center text-gray-700 font-medium">
                        <ShieldCheck className="h-4 w-4 mr-2 text-orange-500" />
                        OTP
                      </span>
                    }
                    name="otp"
                    rules={[
                      { required: true, message: "Please enter the OTP" },
                      () => ({
                        validator(_, value) {
                          const v = String(value || "").trim();
                          if (!/^\d{6}$/.test(v)) {
                            return Promise.reject(
                              new Error("Enter a valid 6-digit OTP")
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="6-digit OTP"
                      className="h-12"
                      inputMode="numeric"
                      maxLength={6}
                    />
                  </Form.Item>
                )}

                <div className="space-y-3">
                  <Button
                    type="primary"
                    size="large"
                    loading={otpRequestResponse?.isLoading || otpVerifyResponse?.isLoading}
                    className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600 font-semibold text-lg flex items-center justify-center"
                    onClick={step === "phone" ? requestOtpClicked : verifyOtpClicked}
                  >
                    {step === "phone" ? "Send OTP" : "Verify & Continue"}
                  </Button>

                  {step === "otp" && (
                    <Button
                      size="large"
                      className="w-full h-12 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                      onClick={() => {
                        setStep("phone");
                        setPhoneSentTo("");
                        form.setFieldsValue({ otp: "" });
                      }}
                    >
                      Change number
                    </Button>
                  )}

                  {step === "otp" && (
                    <Button
                      size="large"
                      className="w-full h-12 border-2 border-orange-200 text-orange-700 hover:bg-orange-50 font-medium"
                      onClick={requestOtpClicked}
                    >
                      Resend OTP
                    </Button>
                  )}
                </div>
              </Form>
            </div>

            {/* Benefits */}
            <div className="mt-8 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-6">
              <h3 className="font-semibold text-gray-800 mb-3 text-center">
                Why Join MahalaxmiPuja?
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                  Book authentic temple pujas from anywhere
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                  Receive blessed prasad at your doorstep
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                  Watch videos of your puja ceremonies
                </li>
              </ul>
            </div>

            {/* Footer */}
            <div className="text-center mt-8">
              <p className="text-gray-500 text-sm">
                🪔 Secure OTP login powered by divine blessings
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="min-h-screen flex items-center justify-center">
          <ServerDownResult />
        </div>
      )}

      <Footer />
    </>
  );
}

export default UserLogin;
